import React from 'react';
import * as TablerIcons from '@tabler/icons-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { borderCssFromProps } from '../../shared/boxes/borderStyles';
import { boxShadowCss } from '../../shared/boxes/shadowStyles';
import { resolveInfoCardIllustration } from '../../shared/boxes/infoCard/infoCardLayout';
import {
  INFO_CARD_DEFAULT_COLOR,
  INFO_CARD_ICON_BACKGROUND_OPACITY,
  infoCardFlexAlign,
  infoCardIconSizePx,
  infoCardIllustrationSizePx,
  infoCardLineGap,
  infoCardLogoHeightPx,
  infoCardQuoteMarkSize,
  infoCardRatingColor,
  infoCardRatingSizePx,
  infoCardRatingStars,
  infoCardTextAlign,
} from '../../shared/boxes/infoCard/infoCardRender';

/**
 * Rendu d'une Carte info dans le document — l'habillage seul.
 *
 * Toutes les valeurs qui décident du rendu viennent du module partagé, comme
 * pour le bloc web : les deux cartes ne peuvent pas diverger sans qu'on
 * change ce module. Seule l'enveloppe est écrite ici, en CSS maison : le
 * document ne charge pas Mantine
 * (docs/webviewer/chantier-boxes-partagees.md §1).
 *
 * Les deux textes sont passés en enfants : éditeurs Draft dans la box,
 * texte d'exemple dans l'aperçu du configurateur.
 */

/** Pastille d'icône : fond teinté sous le glyphe, comme la variante « light ». */
const IconBubble = ({ name, color, sizePx, glyphPx, round }) => {
  const Icon = TablerIcons[name] || TablerIcons.IconStar;
  return (
    <div
      style={{
        position: 'relative',
        width: sizePx,
        height: sizePx,
        flexShrink: 0,
        borderRadius: round ? '50%' : 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {/* Fond en calque séparé : appliquer l'opacité sur la pastille entière
          délaverait aussi le glyphe. */}
      <span
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: color,
          opacity: INFO_CARD_ICON_BACKGROUND_OPACITY,
        }}
      />
      <Icon size={glyphPx} stroke={1.5} color={color} style={{ position: 'relative' }} />
    </div>
  );
};

/** Note sur 5 : rien n'est rendu à 0, comme sur le site. */
const Rating = ({ stars, color, sizePx }) => (
  <div style={{ display: 'flex', gap: 2 }}>
    {[1, 2, 3, 4, 5].map((n) => (
      n <= stars
        ? <TablerIcons.IconStarFilled key={n} size={sizePx} color={color} />
        : <TablerIcons.IconStar key={n} size={sizePx} color="#ced4da" />
    ))}
  </div>
);

const InfoCardFace = ({ style = {}, width, title, description }) => {
  const color = style.color || INFO_CARD_DEFAULT_COLOR;
  const sizePx = infoCardIllustrationSizePx(style.illustrationSize);
  const round = style.illustrationShape !== 'square';
  const align = infoCardFlexAlign(style.contentAlign);
  const gap = infoCardLineGap(style.lineGap);
  const stars = infoCardRatingStars(style.rating);
  const badges = Array.isArray(style.badges) ? style.badges : [];

  // Le document montre le cadre réservé d'une photo manquante, comme
  // l'éditeur de site ; le site publié, lui, n'affiche rien.
  const kind = resolveInfoCardIllustration({ ...style, isEditing: true });

  let illustration = null;
  if (kind === 'icon') {
    illustration = (
      <IconBubble
        name={style.icon}
        color={color}
        sizePx={sizePx}
        glyphPx={infoCardIconSizePx(style.illustrationSize)}
        round={round}
      />
    );
  } else if (kind === 'image') {
    illustration = (
      <img
        src={style.imageUrl}
        alt=""
        style={{
          width: sizePx,
          height: sizePx,
          flexShrink: 0,
          objectFit: 'cover',
          borderRadius: round ? '50%' : 8,
        }}
      />
    );
  } else if (kind === 'placeholder') {
    illustration = (
      <div
        title="Espace réservé — aucune photo choisie, rien ne s'affiche sur le site."
        style={{
          width: sizePx,
          height: sizePx,
          flexShrink: 0,
          borderRadius: round ? '50%' : 8,
          border: '1px dashed #ced4da',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#868e96',
        }}
      >
        <FontAwesomeIcon
          icon={icon({ name: 'image', style: 'light' })}
          style={{ fontSize: Math.round(sizePx * 0.4) }}
        />
      </div>
    );
  }

  const texts = (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: align, gap, width: '100%',
    }}>
      {style.quoteMark && (
        <FontAwesomeIcon
          icon={icon({ name: 'quote-left', style: 'light' })}
          style={{
            fontSize: infoCardQuoteMarkSize(style.titleStyle), color, opacity: 0.35, flexShrink: 0,
          }}
        />
      )}
      {title}
      {description}
      {stars > 0 && (
        <Rating
          stars={stars}
          color={infoCardRatingColor(style)}
          sizePx={infoCardRatingSizePx(style.ratingSize)}
        />
      )}
      {badges.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: align }}>
          {badges.map((badge, index) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 10px',
                borderRadius: 999,
                color: badge.color || color,
                border: `1px solid ${badge.color || color}`,
              }}
            >
              {badge.label}
            </span>
          ))}
        </div>
      )}
      {/* Logo de l'entreprise : jamais recadré, jamais de fond. */}
      {style.logoUrl && (
        <img
          src={style.logoUrl}
          alt=""
          style={{
            height: infoCardLogoHeightPx(style.logoHeight),
            width: 'auto',
            objectFit: 'contain',
          }}
        />
      )}
    </div>
  );

  return (
    <div
      style={{
        width,
        boxSizing: 'border-box',
        padding: 24,
        textAlign: infoCardTextAlign(style.contentAlign),
        ...borderCssFromProps(style.border),
        ...(style.backgroundColor ? { backgroundColor: style.backgroundColor } : {}),
        ...(boxShadowCss(style.shadow) ? { boxShadow: boxShadowCss(style.shadow) } : {}),
      }}
    >
      {/* « Horizontale » tient sa promesse : illustration à gauche, textes à
          droite ; « Verticale » empile tout dans la même colonne. */}
      {style.cardLayout === 'horizontal' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'nowrap' }}>
          {illustration}
          <div style={{ flex: 1, minWidth: 0 }}>{texts}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: align, gap }}>
          {illustration}
          {texts}
        </div>
      )}
    </div>
  );
};

export default InfoCardFace;
